import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import useReactRouter from 'use-react-router';
import qs from 'query-string';
import { pipe, pick, pickBy, complement, isNil, reduce, path } from 'ramda';
import {
  CreditApplication,
  mergeApplication,
  Order,
  ShippingAddressType,
} from 'state/creditApplication';
import canUseDOM from 'utils/canUseDOM';
import * as Yup from 'yup';

type QueryType = { [key in string]: string };

const useLoadInitialCreditApplicationData = (
  initialDataKeys: string[],
): 'unifiedInitialStateError' | undefined => {
  const dispatch = useDispatch();
  const { location } = useReactRouter();

  const query = useMemo(() => {
    return qs.parse(location.search, {
      parseBooleans: true,
    });
  }, [location]);

  if (canUseDOM) {
    // We don't load the query params in browser, so this is always successful.
    return;
  }
  const details = pipe(
    pick(initialDataKeys),
    pickBy((v, _) => complement(isNil)(v) && v),
  )(query) as QueryType;
  const { success, parsed } = parseJsonKeys(
    ['order', 'shippingAddress'] as const,
    details,
  );

  // Replace cardChoiceCode with cardChoice
  if ('cardChoiceCode' in parsed) {
    parsed['cardChoice'] = parsed['cardChoiceCode'];
    delete parsed.cardChoiceCode;
  }

  // Done parsing, so now the value can be considered a CreditApplication
  let prefill = parsed as Partial<CreditApplication>;

  // Set cardChoice to number
  if (prefill.cardChoice) {
    const cardChoiceNumber = parseInt(prefill.cardChoice.toString());
    if (!isNaN(cardChoiceNumber)) {
      prefill.cardChoice = cardChoiceNumber;
    } else {
      // We can't parse cardChoice as an int so we set it back to null
      prefill.cardChoice = null;
    }
  }

  prefill = setIntendedPurchaseAmount(prefill);

  dispatch(mergeApplication(prefill));
  return success &&
    isValidSchema(
      parsed.shippingAddress as ShippingAddressType,
      parsed.order as Order,
    )
    ? undefined
    : 'unifiedInitialStateError';
};

const setIntendedPurchaseAmount = (
  app: Partial<CreditApplication>,
): Partial<CreditApplication> => {
  const cascadedAmount =
    app['checkoutAmount'] ||
    app['cartAmount'] ||
    app['productAmount'] ||
    path(['order', 'totalPriceValue'], app);

  const withPurchaseAmount = cascadedAmount
    ? // Order totalPrice is a number, but we still expect intendedPurchaseAmount
      // to be a string because it's represented by TextFields
      { intendedPurchaseAmount: cascadedAmount.toString() }
    : {};

  return { ...app, ...withPurchaseAmount };
};

const isValidSchema = (
  shippingAddress: ShippingAddressType,
  order: Order,
): boolean => {
  const schema = Yup.object().shape({
    shippingAddress: Yup.object()
      .shape({
        firstName: Yup.string(), // Signals that it should be type string
        lastName: Yup.string(),
        city: Yup.string().required(),
        state: Yup.string().required(),
        zip: Yup.string().required(),
        address1: Yup.string().required(),
        address2: Yup.string(),
        emailAddress: Yup.string(),
        mobilePhone: Yup.string(),
      })
      .nullable(),
    order: Yup.object()
      .shape({
        totalPriceValue: Yup.number()
          .min(0)
          .required(),
        subTotalValue: Yup.number().required(),
        totalDiscountsValue: Yup.number().required(),
        totalShippingValue: Yup.number().required(),
        totalTaxValue: Yup.number().required(),
        discountCode: Yup.string(),
        shippingProvider: Yup.string(),
        shippingDescription: Yup.string(),
        shippingTrackingNumber: Yup.string(),
        shippingTrackingUrl: Yup.string(),
        items: Yup.array().of(
          Yup.object().shape({
            name: Yup.string().required(),
            quantity: Yup.number()
              .min(1)
              .required(),
            unitPriceValue: Yup.number().required(),
            unitTaxValue: Yup.number().required(),
            category: Yup.string(),
            sku: Yup.string(),
            itemUrl: Yup.string(),
            imageUrl: Yup.string(),
            description: Yup.string(),
            shippingCostValue: Yup.number(),
          }),
        ),
      })
      .nullable(),
  });
  return schema.isValidSync(
    { shippingAddress: shippingAddress, order: order },
    { strict: true },
  );
};

/**
 * Return the same object with value at the specified keys parsed as JSON string.
 *
 * If a value is undefined, that key will be ignored.
 * If a value is a valid JSON string, it will be parsed
 * If a value is not a valid JSON string, it will be set to null
 *
 * If any value is not a valid JSON string, success is false.
 */
const parseJsonKeys = <K extends string>(
  keys: readonly K[],
  original: QueryType,
): {
  success: boolean;
  parsed: QueryType & { [k in K]: unknown };
} => {
  const [success, result] = reduce(
    ([success, details], key) => {
      const value = details[key];
      if (value === undefined) {
        return [success, details];
      }
      try {
        const parsed = JSON.parse(value);
        return [success, { ...details, [key]: parsed }];
      } catch {
        return [false, { ...details, [key]: null }];
      }
    },
    [true, original],
    keys as K[],
  );
  return {
    success,
    parsed: result as QueryType & { [k in K]: unknown },
  };
};

export default useLoadInitialCreditApplicationData;
